import React, { useEffect, useMemo, useState } from "react";
import { PaystackButton } from "react-paystack";
import {
  Search,
  ShoppingCart,
  Plus,
  Minus,
  Trash2,
  X,
  Package,
  ArrowRight,
  ShoppingBag,
  ShieldCheck,
  CreditCard,
} from "lucide-react";
import Swal from "sweetalert2";
import api from "../Api/Api";

const Shop = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [cart, setCart] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);

  const user_id = localStorage.getItem("user_id");

  // Recommended:
  // VITE_PAYSTACK_PUBLIC_KEY=pk_test_xxxxx
  const publicKey =
    process.env.REACT_APP_PAYSTACK_PUBLIC_KEY ||
    "pk_test_6633ec1991d6ba92490835f6cbc1b7934876a55f";

  useEffect(() => {
    const fetchProducts = async () => {
      const storedEmail = localStorage.getItem("email");

      if (storedEmail) {
        setEmail(storedEmail);
      }

      try {
        const response = await api.get("/get_products/");

        setProducts(
          Array.isArray(response.data?.products)
            ? response.data.products
            : []
        );
      } catch (error) {
        console.error("Failed to load products:", error);

        Swal.fire({
          icon: "error",
          title: "Unable to Load Products",
          text:
            error.response?.data?.message ||
            "We couldn't load the shop products. Please try again later.",
          confirmButtonColor: "#2563EB",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const formatPrice = (price) => {
    return Number(price || 0).toLocaleString("en-KE");
  };

  const addToCart = (product) => {
    setCart((prev) => {
      const existingProduct = prev.find(
        (item) => item.id === product.id
      );

      if (existingProduct) {
        return prev.map((item) =>
          item.id === product.id
            ? {
                ...item,
                quantity: item.quantity + 1,
              }
            : item
        );
      }

      return [
        ...prev,
        {
          ...product,
          quantity: 1,
        },
      ];
    });

    Swal.fire({
      icon: "success",
      title: "Added to Cart",
      text: `${product.name} has been added to your cart.`,
      showConfirmButton: false,
      timer: 1200,
    });
  };

  const increaseQuantity = (productId) => {
    setCart((prev) =>
      prev.map((item) =>
        item.id === productId
          ? {
              ...item,
              quantity: item.quantity + 1,
            }
          : item
      )
    );
  };

  const decreaseQuantity = (productId) => {
    setCart((prev) =>
      prev
        .map((item) =>
          item.id === productId
            ? {
                ...item,
                quantity: item.quantity - 1,
              }
            : item
        )
        .filter((item) => item.quantity > 0)
    );
  };

  const removeFromCart = (productId) => {
    setCart((prev) =>
      prev.filter((item) => item.id !== productId)
    );
  };

  const cartItemsCount = useMemo(() => {
    return cart.reduce(
      (total, item) => total + item.quantity,
      0
    );
  }, [cart]);

  const totalAmount = useMemo(() => {
    return cart.reduce(
      (sum, item) =>
        sum + Number(item.price) * item.quantity,
      0
    );
  }, [cart]);

  const payProduct = async (
    product_id,
    product_name,
    price
  ) => {
    if (!user_id) {
      Swal.fire({
        icon: "warning",
        title: "Sign In Required",
        text: "Please sign in before buying a product.",
        confirmButtonColor: "#2563EB",
      });

      return;
    }

    try {
      const response = await api.post(
        "/create_product_order/",
        {
          product_id,
          user_id,
          product_name,
          quantity: 1,
          price,
        }
      );

      if (
        response.status === 200 ||
        response.status === 201
      ) {
        setSelectedProduct(null);

        Swal.fire({
          icon: "success",
          title: "Order Successful",
          text: "Your order has been placed successfully.",
          confirmButtonColor: "#2563EB",
        });
      }
    } catch (error) {
      console.error("Error creating product order:", error);

      Swal.fire({
        icon: "error",
        title: "Order Failed",
        text:
          error.response?.data?.message ||
          "There was an error placing your order.",
        confirmButtonColor: "#2563EB",
      });
    }
  };

  const handleCheckout = async () => {
    if (!user_id) {
      Swal.fire({
        icon: "warning",
        title: "Sign In Required",
        text: "Please sign in before checking out.",
        confirmButtonColor: "#2563EB",
      });

      return;
    }

    if (cart.length === 0) {
      return;
    }

    try {
      const response = await api.post(
        "/create_bulk_order/",
        {
          user_id,
          products: cart.map((item) => ({
            product_id: item.id,
            product_name: item.name,
            quantity: item.quantity,
            price: item.price,
          })),
        }
      );

      if (
        response.status === 200 ||
        response.status === 201
      ) {
        setCart([]);
        setCartOpen(false);

        Swal.fire({
          icon: "success",
          title: "Order Confirmed",
          text: "Your order has been placed successfully.",
          confirmButtonColor: "#2563EB",
        });
      }
    } catch (error) {
      console.error("Checkout error:", error);

      Swal.fire({
        icon: "error",
        title: "Checkout Failed",
        text:
          error.response?.data?.message ||
          "There was an error completing your order.",
        confirmButtonColor: "#2563EB",
      });
    }
  };

  const filteredProducts = useMemo(() => {
    const search = searchTerm.toLowerCase().trim();

    if (!search) return products;

    return products.filter((product) => {
      const name = product.name?.toLowerCase() || "";
      const category =
        product.category?.toLowerCase() || "";
      const description =
        product.description?.toLowerCase() || "";

      return (
        name.includes(search) ||
        category.includes(search) ||
        description.includes(search)
      );
    });
  }, [products, searchTerm]);

  const groupedProducts = useMemo(() => {
    return filteredProducts.reduce(
      (groups, product) => {
        const category =
          product.category || "Others";

        if (!groups[category]) {
          groups[category] = [];
        }

        groups[category].push(product);

        return groups;
      },
      {}
    );
  }, [filteredProducts]);

  const selectedProductPaystackProps =
    selectedProduct
      ? {
          email,
          amount:
            Number(selectedProduct.price) * 100,
          metadata: {
            product_name: selectedProduct.name,
          },
          publicKey,
          currency: "KES",
          text: "Pay Now",
          onSuccess: () =>
            payProduct(
              selectedProduct.id,
              selectedProduct.name,
              selectedProduct.price
            ),
          onClose: () => {
            Swal.fire({
              icon: "info",
              title: "Payment Cancelled",
              text: "The payment was cancelled.",
              confirmButtonColor: "#2563EB",
            });

            setSelectedProduct(null);
          },
        }
      : {};

  const bulkPaystackProps = {
    email,
    amount: totalAmount * 100,
    publicKey,
    text: "Checkout Securely",
    currency: "KES",
    metadata: {
      cart: cart.map((item) => ({
        product_name: item.name,
        quantity: item.quantity,
      })),
    },
    onSuccess: handleCheckout,
    onClose: () => {
      Swal.fire({
        icon: "info",
        title: "Payment Cancelled",
        text: "Your cart is still available.",
        confirmButtonColor: "#2563EB",
      });
    },
  };

  const handleBuyNow = (product) => {
    if (!user_id) {
      Swal.fire({
        icon: "warning",
        title: "Sign In Required",
        text: "Please sign in before buying a product.",
        confirmButtonColor: "#2563EB",
      });

      return;
    }

    if (!email) {
      Swal.fire({
        icon: "warning",
        title: "Email Required",
        text: "Your account needs a valid email address before payment.",
        confirmButtonColor: "#2563EB",
      });

      return;
    }

    setSelectedProduct(product);
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      {/* Hero */}
      <section className="relative bg-slate-950 text-white pt-24 pb-28 px-6 overflow-hidden">
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-blue-600/20 rounded-full blur-[150px]" />

        <div className="absolute -bottom-40 right-0 w-[500px] h-[500px] bg-cyan-400/10 rounded-full blur-[140px]" />

        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.7) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.7) 1px, transparent 1px)
            `,
            backgroundSize: "50px 50px",
          }}
        />

        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-[1fr_auto] gap-10 items-end">
            <div className="max-w-4xl">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-blue-400/20 bg-blue-500/10 text-blue-300 text-sm font-semibold mb-6">
                <ShoppingBag size={15} />
                Nexindi Shop
              </div>

              <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-tight mb-6">
                Technology products for
                <span className="block text-blue-400">
                  work and everyday life.
                </span>
              </h1>

              <p className="text-lg md:text-xl text-slate-300 max-w-3xl leading-relaxed">
                Explore technology products and solutions available
                through Nexindi Tech, with secure online payments
                and simple ordering.
              </p>
            </div>

            <div className="flex gap-4">
              <div className="bg-white/10 border border-white/10 backdrop-blur-md rounded-2xl p-5 min-w-[150px]">
                <Package
                  size={20}
                  className="text-blue-300 mb-3"
                />

                <p className="text-2xl font-bold">
                  {products.length}
                </p>

                <p className="text-xs text-slate-400 mt-1">
                  Products available
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Shop */}
      <main className="relative -mt-10 pb-28 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Search */}
          <div className="bg-white rounded-3xl border border-slate-200 shadow-xl shadow-slate-900/5 p-5 md:p-6 mb-14">
            <div className="flex flex-col md:flex-row md:items-center gap-4">
              <div className="relative flex-1">
                <Search
                  size={20}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                />

                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) =>
                    setSearchTerm(e.target.value)
                  }
                  placeholder="Search products, categories..."
                  className="w-full pl-12 pr-4 py-4 rounded-xl border border-slate-200 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                />
              </div>

              <button
                onClick={() => setCartOpen(true)}
                className="relative flex items-center justify-center gap-3 bg-blue-600 text-white px-6 py-4 rounded-xl font-semibold hover:bg-blue-700 transition"
              >
                <ShoppingCart size={19} />

                My Cart

                {cartItemsCount > 0 && (
                  <span className="min-w-6 h-6 px-1.5 rounded-full bg-white text-blue-600 text-xs font-bold flex items-center justify-center">
                    {cartItemsCount}
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* Loading */}
          {loading && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[1, 2, 3, 4, 5, 6, 7, 8].map(
                (item) => (
                  <div
                    key={item}
                    className="rounded-3xl border border-slate-200 bg-white overflow-hidden animate-pulse"
                  >
                    <div className="h-56 bg-slate-200" />

                    <div className="p-6">
                      <div className="h-5 bg-slate-200 rounded mb-4" />
                      <div className="h-4 w-1/2 bg-slate-200 rounded mb-8" />
                      <div className="h-11 bg-slate-200 rounded-xl" />
                    </div>
                  </div>
                )
              )}
            </div>
          )}

          {/* Empty */}
          {!loading &&
            Object.keys(groupedProducts).length === 0 && (
              <div className="bg-white border border-slate-200 rounded-3xl py-20 px-6 text-center">
                <div className="w-16 h-16 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center mx-auto mb-6">
                  <Search size={27} />
                </div>

                <h2 className="text-2xl font-bold mb-3">
                  No products found
                </h2>

                <p className="text-slate-500">
                  Try changing your search term.
                </p>
              </div>
            )}

          {/* Categories */}
          {!loading &&
            Object.entries(groupedProducts).map(
              ([category, categoryProducts]) => (
                <section
                  key={category}
                  className="mb-20"
                >
                  <div className="flex items-end justify-between gap-5 mb-8">
                    <div>
                      <p className="text-blue-600 text-sm font-semibold mb-2">
                        Product Category
                      </p>

                      <h2 className="text-3xl md:text-4xl font-bold tracking-tight">
                        {category}
                      </h2>
                    </div>

                    <span className="text-sm text-slate-400">
                      {categoryProducts.length}{" "}
                      {categoryProducts.length === 1
                        ? "product"
                        : "products"}
                    </span>
                  </div>

                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {categoryProducts.map(
                      (product) => (
                        <article
                          key={product.id}
                          className="group flex flex-col bg-white border border-slate-200 rounded-3xl overflow-hidden transition-all duration-500 hover:-translate-y-2 hover:border-blue-200 hover:shadow-2xl hover:shadow-blue-900/10"
                        >
                          {/* Image */}
                          <div className="relative h-60 overflow-hidden bg-slate-100">
                            {product.image ? (
                              <img
                                src={product.image}
                                alt={product.name}
                                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Package
                                  size={40}
                                  className="text-slate-300"
                                />
                              </div>
                            )}

                            <div className="absolute inset-0 bg-gradient-to-t from-slate-950/20 to-transparent" />

                            <span className="absolute top-4 left-4 px-3 py-1.5 rounded-full bg-white/90 backdrop-blur-md text-xs font-semibold text-blue-600">
                              {category}
                            </span>
                          </div>

                          {/* Content */}
                          <div className="flex flex-col flex-1 p-6">
                            <h3 className="text-xl font-bold text-slate-900 group-hover:text-blue-600 transition mb-3">
                              {product.name}
                            </h3>

                            {product.description && (
                              <p className="text-sm text-slate-500 leading-relaxed line-clamp-2 mb-5">
                                {product.description}
                              </p>
                            )}

                            <div className="mt-auto">
                              <p className="text-xs uppercase tracking-wider text-slate-400 mb-1">
                                Price
                              </p>

                              <p className="text-2xl font-bold text-blue-600 mb-6">
                                KES{" "}
                                {formatPrice(
                                  product.price
                                )}
                              </p>

                              <div className="grid grid-cols-2 gap-3">
                                <button
                                  onClick={() =>
                                    addToCart(product)
                                  }
                                  className="flex items-center justify-center gap-2 border border-slate-200 text-slate-700 py-3 rounded-xl font-semibold text-sm hover:border-blue-200 hover:bg-blue-50 hover:text-blue-600 transition"
                                >
                                  <ShoppingCart
                                    size={16}
                                  />
                                  Add
                                </button>

                                <button
                                  onClick={() =>
                                    handleBuyNow(product)
                                  }
                                  className="group/button flex items-center justify-center gap-2 bg-blue-600 text-white py-3 rounded-xl font-semibold text-sm hover:bg-blue-700 transition"
                                >
                                  Buy Now

                                  <ArrowRight
                                    size={15}
                                    className="transition-transform group-hover/button:translate-x-1"
                                  />
                                </button>
                              </div>
                            </div>
                          </div>
                        </article>
                      )
                    )}
                  </div>
                </section>
              )
            )}
        </div>
      </main>

      {/* Buy Now Payment Modal */}
      {selectedProduct && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/60 backdrop-blur-sm px-6">
          <div className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl p-7">
            <button
              onClick={() =>
                setSelectedProduct(null)
              }
              className="absolute top-5 right-5 w-10 h-10 rounded-xl bg-slate-100 text-slate-500 flex items-center justify-center hover:bg-slate-200 transition"
            >
              <X size={18} />
            </button>

            <div className="w-14 h-14 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center mb-6">
              <CreditCard size={24} />
            </div>

            <p className="text-sm text-blue-600 font-semibold mb-2">
              Secure Payment
            </p>

            <h2 className="text-2xl font-bold mb-3">
              {selectedProduct.name}
            </h2>

            <p className="text-slate-500 mb-8">
              Complete your payment securely to
              place this order.
            </p>

            <div className="flex items-center justify-between border-y border-slate-200 py-5 mb-7">
              <span className="text-slate-500">
                Total
              </span>

              <span className="text-2xl font-bold text-blue-600">
                KES{" "}
                {formatPrice(
                  selectedProduct.price
                )}
              </span>
            </div>

            <PaystackButton
              {...selectedProductPaystackProps}
              className="w-full bg-blue-600 text-white py-3.5 rounded-xl font-semibold hover:bg-blue-700 transition"
            />

            <div className="flex items-center justify-center gap-2 mt-5 text-xs text-slate-400">
              <ShieldCheck size={14} />
              Secure payment powered by Paystack
            </div>
          </div>
        </div>
      )}

      {/* Cart Overlay */}
      {cartOpen && (
        <div className="fixed inset-0 z-[80]">
          <div
            className="absolute inset-0 bg-slate-950/50 backdrop-blur-sm"
            onClick={() => setCartOpen(false)}
          />

          <div className="absolute right-0 top-0 bottom-0 w-full sm:max-w-lg bg-white shadow-2xl flex flex-col">
            {/* Cart Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-slate-200">
              <div>
                <h2 className="text-2xl font-bold">
                  Your Cart
                </h2>

                <p className="text-sm text-slate-500 mt-1">
                  {cartItemsCount}{" "}
                  {cartItemsCount === 1
                    ? "item"
                    : "items"}
                </p>
              </div>

              <button
                onClick={() => setCartOpen(false)}
                className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center hover:bg-slate-200 transition"
              >
                <X size={19} />
              </button>
            </div>

            {/* Items */}
            <div className="flex-1 overflow-y-auto p-6">
              {cart.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center">
                  <div className="w-16 h-16 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center mb-6">
                    <ShoppingCart size={28} />
                  </div>

                  <h3 className="text-xl font-bold mb-2">
                    Your cart is empty
                  </h3>

                  <p className="text-slate-500">
                    Add products from the shop to
                    continue.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {cart.map((item) => (
                    <div
                      key={item.id}
                      className="border border-slate-200 rounded-2xl p-4"
                    >
                      <div className="flex gap-4">
                        <div className="w-20 h-20 rounded-xl bg-slate-100 overflow-hidden flex-shrink-0">
                          {item.image ? (
                            <img
                              src={item.image}
                              alt={item.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Package
                                size={24}
                                className="text-slate-300"
                              />
                            </div>
                          )}
                        </div>

                        <div className="flex-1">
                          <div className="flex justify-between gap-3">
                            <div>
                              <h3 className="font-bold text-slate-900">
                                {item.name}
                              </h3>

                              <p className="text-blue-600 font-semibold text-sm mt-1">
                                KES{" "}
                                {formatPrice(
                                  item.price
                                )}
                              </p>
                            </div>

                            <button
                              onClick={() =>
                                removeFromCart(
                                  item.id
                                )
                              }
                              className="w-9 h-9 rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-600 flex items-center justify-center transition"
                            >
                              <Trash2
                                size={16}
                              />
                            </button>
                          </div>

                          <div className="flex items-center justify-between mt-4">
                            <div className="flex items-center border border-slate-200 rounded-xl overflow-hidden">
                              <button
                                onClick={() =>
                                  decreaseQuantity(
                                    item.id
                                  )
                                }
                                className="w-9 h-9 flex items-center justify-center hover:bg-slate-100"
                              >
                                <Minus
                                  size={14}
                                />
                              </button>

                              <span className="w-9 text-center text-sm font-semibold">
                                {item.quantity}
                              </span>

                              <button
                                onClick={() =>
                                  increaseQuantity(
                                    item.id
                                  )
                                }
                                className="w-9 h-9 flex items-center justify-center hover:bg-slate-100"
                              >
                                <Plus
                                  size={14}
                                />
                              </button>
                            </div>

                            <p className="font-bold">
                              KES{" "}
                              {formatPrice(
                                Number(
                                  item.price
                                ) *
                                  item.quantity
                              )}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Checkout */}
            {cart.length > 0 && (
              <div className="border-t border-slate-200 p-6 bg-slate-50">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-slate-500">
                    Items
                  </span>

                  <span>
                    {cartItemsCount}
                  </span>
                </div>

                <div className="flex items-center justify-between mb-6">
                  <span className="text-lg font-bold">
                    Total
                  </span>

                  <span className="text-2xl font-bold text-blue-600">
                    KES{" "}
                    {formatPrice(
                      totalAmount
                    )}
                  </span>
                </div>

                <PaystackButton
                  {...bulkPaystackProps}
                  className="w-full flex items-center justify-center bg-blue-600 text-white px-6 py-4 rounded-xl font-semibold hover:bg-blue-700 transition"
                />

                <div className="flex items-center justify-center gap-2 mt-4 text-xs text-slate-400">
                  <ShieldCheck size={14} />
                  Secure checkout powered by Paystack
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Shop;